// REPLACE: src/components/chapter-intro-slide.jsx
//
// Chapter-start intro, YouTube-Shorts style: full-bleed teacher photo with
// a cinematic Ken Burns slow zoom/drift, tap-anywhere to toggle play/pause,
// narration auto-starts on first arrival. Auto-advances when audio ends.
//
// Ken Burns notes:
// - A single base class holds the animation declaration. State classes only
//   toggle `animation-play-state` (or null out `animation-name` to freeze at
//   idle/ended). That's what makes pause/resume continue from the current
//   frame instead of snapping back to 0%.
// - `animation-duration` is driven from the audio's real length via an
//   inline style — so a 15-sec or 30-sec file auto-matches. Defaults to 20s
//   until `loadedmetadata` fires.
// - Revisit: when a chapter's intro has finished once (flag in
//   localStorage), the photo mounts in its final zoomed state and does not
//   auto-play. Tapping then restarts from scale 1.0.

import { useEffect, useRef, useState } from "react";
import soundManager from "../sounds/sound-manager";

const PLAYED_KEY_PREFIX = "samajh_intro_played_";
const DEFAULT_DURATION_S = 20;

export function ChapterIntroSlide({ image, audio, title, chapterKey, onComplete, onSkip }) {
  const audioRef = useRef(null);
  const autoPlayTriedRef = useRef(false);

  const storageKey = chapterKey ? `${PLAYED_KEY_PREFIX}${chapterKey}` : null;
  const alreadyEnded = (() => {
    try { return storageKey ? localStorage.getItem(storageKey) === "1" : false; }
    catch { return false; }
  })();

  // 'idle' | 'playing' | 'paused' | 'ended'
  const [audioState, setAudioState] = useState(alreadyEnded ? "ended" : "idle");
  const [audioDuration, setAudioDuration] = useState(DEFAULT_DURATION_S);
  const [hasError, setHasError] = useState(false);

  // Build the Audio element eagerly on first render so the user gesture
  // that mounted the slide is still "live" when we call .play().
  if (!audioRef.current && audio && typeof Audio !== "undefined") {
    try {
      const a = new Audio(audio);
      a.preload = "auto";
      audioRef.current = a;
    } catch { /* SSR / unsupported — remains null */ }
  }

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return undefined;

    const onMeta = () => {
      if (Number.isFinite(a.duration) && a.duration > 0) {
        setAudioDuration(a.duration);
      }
    };
    const onPlay = () => setAudioState("playing");
    const onPause = () => {
      // 'pause' fires on its own AND right before 'ended'. Ignore the
      // natural-end pause so the Ken Burns doesn't flash the paused-class
      // for one frame before landing on 'ended'.
      if (!a.ended) setAudioState("paused");
    };
    const onEnded = () => {
      setAudioState("ended");
      try { if (storageKey) localStorage.setItem(storageKey, "1"); } catch { /* no-op */ }
      setTimeout(() => { onComplete?.(); }, 500);
    };
    const onError = () => { setHasError(true); setAudioState("idle"); };

    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnded);
    a.addEventListener("error", onError);

    // If the browser already has metadata cached (e.g., when revisiting and
    // the blob is warm), fire the handler once so duration isn't stuck on
    // the default.
    if (Number.isFinite(a.duration) && a.duration > 0) onMeta();

    // Auto-play only on first arrival (not on revisit-after-end).
    if (!autoPlayTriedRef.current && !alreadyEnded) {
      autoPlayTriedRef.current = true;
      let muted = false;
      try { muted = soundManager?.isMuted?.() === true; } catch { /* no-op */ }
      if (!muted) {
        const p = a.play();
        if (p && typeof p.then === "function") {
          p.catch(() => { /* autoplay blocked — tap overlay prompts user */ });
        }
      }
    }

    return () => {
      try { a.pause(); } catch { /* no-op */ }
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnded);
      a.removeEventListener("error", onError);
      audioRef.current = null;
    };
  }, [audio, storageKey, alreadyEnded]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (e) => {
    if (e?.target?.dataset?.skip === "1") return;
    const a = audioRef.current;
    if (!a) return;

    // From 'ended' → restart from the beginning (rewinds audio + Ken Burns).
    if (audioState === "ended" || a.ended) {
      try { a.currentTime = 0; } catch { /* no-op */ }
      const p = a.play();
      if (p && typeof p.then === "function") p.catch(() => {});
      return;
    }

    if (a.paused) {
      const p = a.play();
      if (p && typeof p.then === "function") p.catch(() => {});
    } else {
      a.pause();
    }
  };

  const skip = (e) => {
    e?.stopPropagation?.();
    const a = audioRef.current;
    if (a) { try { a.pause(); } catch { /* no-op */ } }
    onSkip?.();
  };

  const photoClass = `samajh-intro-photo ${audioState}`;
  const showPlayOverlay = audioState !== "playing";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={toggle}
      onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); toggle(e); } }}
      style={{
        flex: 1,
        width: "100%",
        position: "relative",
        overflow: "hidden",
        background: "#000",
        cursor: "pointer",
        userSelect: "none",
        WebkitTapHighlightColor: "transparent",
        outline: "none",
      }}
    >
      {/* Full-bleed photo with Ken Burns.
          animationDuration is set inline so it syncs with the real audio length. */}
      <img
        src={image}
        alt=""
        draggable={false}
        className={photoClass}
        style={{ animationDuration: `${audioDuration}s` }}
        onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
      />

      {/* Subtle gradient for overlay legibility */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 28%, rgba(0,0,0,0) 62%, rgba(0,0,0,0.45) 100%)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      {/* Skip intro → */}
      <button
        type="button"
        data-skip="1"
        onClick={skip}
        style={{
          position: "absolute",
          top: 14,
          right: 14,
          background: "rgba(0,0,0,0.45)",
          color: "white",
          border: "none",
          borderRadius: 999,
          padding: "8px 14px",
          fontSize: 13,
          fontWeight: 600,
          fontFamily: "Poppins, sans-serif",
          cursor: "pointer",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          zIndex: 3,
        }}
      >
        Skip intro →
      </button>

      {/* Chapter title */}
      <div
        style={{
          position: "absolute",
          left: 20,
          right: 20,
          bottom: 28,
          color: "white",
          fontFamily: "Poppins, sans-serif",
          fontSize: 22,
          fontWeight: 700,
          letterSpacing: "-0.01em",
          lineHeight: 1.25,
          textShadow: "0 2px 10px rgba(0,0,0,0.45)",
          pointerEvents: "none",
          zIndex: 2,
        }}
      >
        {title}
      </div>

      {/* Big centered play overlay — appears when paused/idle/ended; hides while playing */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: showPlayOverlay ? 1 : 0,
          transition: "opacity 0.25s ease",
          pointerEvents: "none",
          zIndex: 2,
        }}
      >
        <div
          style={{
            width: 84,
            height: 84,
            borderRadius: "50%",
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
          }}
        >
          <svg width="34" height="38" viewBox="0 0 14 16" fill="white" aria-hidden="true">
            <path d="M2 1.5 L13 8 L2 14.5 Z" />
          </svg>
        </div>
      </div>

      {hasError && (
        <div
          style={{
            position: "absolute",
            bottom: 78,
            left: 20,
            right: 20,
            color: "rgba(255,255,255,0.85)",
            fontFamily: "Poppins, sans-serif",
            fontSize: 12,
            textAlign: "center",
            textShadow: "0 1px 4px rgba(0,0,0,0.4)",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          Audio couldn't load. Tap Skip to continue.
        </div>
      )}

      {/* Ken Burns styles. The base rule declares the whole animation so
          pause/resume (via `animation-play-state` alone) continues from the
          same frame. idle/ended null out `animation-name` so they can pin
          explicit transforms (start / end of the keyframe). */}
      <style>{`
        @keyframes samajhKenBurns {
          0%   { transform: scale(1.0) translate(0, 0); }
          100% { transform: scale(1.08) translate(-2%, -3%); }
        }
        .samajh-intro-photo {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          pointer-events: none;
          transform-origin: center center;
          will-change: transform;
          animation-name: samajhKenBurns;
          animation-duration: ${DEFAULT_DURATION_S}s;
          animation-timing-function: cubic-bezier(0.4, 0.0, 0.6, 1.0);
          animation-iteration-count: 1;
          animation-fill-mode: forwards;
          animation-play-state: paused;
        }
        .samajh-intro-photo.playing {
          animation-play-state: running;
        }
        .samajh-intro-photo.paused {
          animation-play-state: paused;
        }
        .samajh-intro-photo.idle {
          animation-name: none;
          transform: scale(1.0) translate(0, 0);
        }
        .samajh-intro-photo.ended {
          animation-name: none;
          transform: scale(1.08) translate(-2%, -3%);
        }
      `}</style>
    </div>
  );
}

export default ChapterIntroSlide;
